import React from 'react';
import { useHistory } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import Icon from '@common/components/icon';
import Tooltip from '@common/components/tooltip';
import { useThunkDispatch } from '@hooks/redux';
import { useSearchParams } from '@common/hooks/use-search-params';
import { combineClassNames } from '@utils/combineClassNames';
import type { FailedRequest } from '@common/services/api';
import { createTrialWithResearch } from '../../actions';
import { useTrialResearch } from '../../hooks/use-trial-research';
import { buildingPath } from '../../utils/trial-route';
import { ETrialIndustrySets, type TrialResearchPreview } from '../../types';
import { StepIndicator } from './step-indicator';
import { Testimonials } from './testimonials';
import { DomainStep } from './domain-step';
import { ResearchPanel } from './research-panel';
import { PreviewStep, toEditableLocations, type PreviewValues } from './preview-step';
import { AppPreview } from './app-preview';
import { DetailsStep, type DetailsErrorKind } from './details-step';
import { HeroPanel } from './hero-panel';

type Step = 'domain' | 'researching' | 'preview' | 'details';

const STEP_NUMBER: Record<Step, number> = {
  domain: 1, researching: 1, preview: 2, details: 3,
};

const PHOTO: Record<Step, string> = {
  domain: 'team-restaurant',
  researching: 'manager-tablet',
  preview: 'restaurant-setting-tables',
  details: 'team-restaurant',
};

const initialValues = (preview: TrialResearchPreview, languageLocale: 'nl' | 'en'): PreviewValues => ({
  name: preview.name,
  industrySet: preview.industry_proposed ?? ETrialIndustrySets.BASE,
  languageLocale,
  locations: toEditableLocations(preview),
  modules: [],
});

const detailsErrorOf = (error: FailedRequest): DetailsErrorKind => {
  if (error.status_code === 403) return 'existing';
  if (error.status_code === 429) return 'rate_limited';
  if (error.status_code === 422 && /research_token/.test(error.detail || '')) return 'token';

  return 'failed';
};

/**
 * Landing-page trial signup: website → research → confirm what we found →
 * name and email → straight into the progress screen with a session.
 */
export const FreeTrialContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useThunkDispatch();
  const { searchParams } = useSearchParams<{ email?: string; domain?: string; lang?: string }>();
  const languageLocale: 'nl' | 'en' = searchParams.lang === 'nl' ? 'nl' : 'en';

  const research = useTrialResearch({ kind: 'anonymous', languageLocale });
  const [step, setStep] = React.useState<Step>('domain');
  const [domain, setDomain] = React.useState(searchParams.domain || '');
  const [values, setValues] = React.useState<PreviewValues | null>(null);
  const [detailsError, setDetailsError] = React.useState<DetailsErrorKind | null>(null);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const { preview } = research;

  // Research finished: show the preview; failed: back to the website step with the reason.
  React.useEffect(() => {
    if (step !== 'researching') return;

    if (research.isDone && preview) {
      setValues(initialValues(preview, languageLocale));
      setStep('preview');
    } else if (research.error) {
      setStep('domain');
    }
  }, [step, research.isDone, research.error, preview, languageLocale]);

  const startResearch = async (nextDomain: string) => {
    setDomain(nextDomain);
    setStep('researching');

    const started = await research.start(nextDomain);

    if (!started) setStep('domain');
  };

  const restart = () => {
    research.reset();
    setValues(null);
    setDetailsError(null);
    setStep('domain');
  };

  const submit = async ({ firstName, email, password }: { firstName: string; email: string; password: string }) => {
    if (!values || !research.token) return;

    setSubmitting(true);
    setDetailsError(null);

    try {
      const response = await dispatch(createTrialWithResearch({
        email,
        first_name: firstName,
        password,
        research_token: research.token,
        name: values.name.trim(),
        industry_set: values.industrySet,
        language_locale: values.languageLocale,
        locations: values.locations
          .filter((location) => location.selected)
          .map((location) => ({
            place_id: location.place_id,
            name: location.name.trim(),
            is_head_office: location.is_head_office,
          })),
        modules_of_interest: values.modules,
      }));

      history.replace(buildingPath(response.organisation_id), { generation: response.generation, domain });
    } catch (err) {
      setDetailsError(detailsErrorOf(err as FailedRequest));
      setSubmitting(false);
    }
  };

  return (
    <div id="Login" className={combineClassNames('FreeTrial', `FreeTrial--step-${step}`)}>
      <div className="Authentication__Container">
        <div className="Authentication__Form">
          <img className="Authentication__Form__Logo" src="/static/images/logo.png" alt="Oneteam" />
          <StepIndicator current={STEP_NUMBER[step]} total={3} />

          {step === 'domain' && (
            <DomainStep
              initialValue={domain}
              error={research.error}
              isSubmitting={research.isStarting}
              onSubmit={startResearch}
            />
          )}

          {step === 'researching' && (
            <>
              <h2>{t('authentication:trial_researching_title', { domain })}</h2>
              <div className="Authentication__Form__Description">{t('authentication:trial_researching_description')}</div>
              <div className="Authentication__Form__Action">
                <a onClick={restart} role="link">{t('authentication:trial_researching_change_website')}</a>
              </div>
              <div className="Authentication__Form__Consent">{t('authentication:trial_researching_note')}</div>
            </>
          )}

          {step === 'preview' && preview && values && (
            <PreviewStep
              domain={domain}
              preview={preview}
              values={values}
              onChange={setValues}
              onContinue={() => setStep('details')}
              onReject={restart}
            />
          )}

          {step === 'details' && values && (
            <DetailsStep
              companyName={values.name}
              initialEmail={searchParams.email || ''}
              error={detailsError}
              isSubmitting={isSubmitting}
              onSubmit={submit}
              onBack={() => setStep('preview')}
            />
          )}
        </div>
        <Tooltip placement="top" title={t('authentication:login_encrypted_tooltip')}>
          <div className="Authentication__Form__Secure">
            <Icon type="lock" />
            <Trans i18nKey="authentication:secure_connection" />
          </div>
        </Tooltip>
      </div>

      <div className={combineClassNames('FreeTrial__Container', `FreeTrial__Container--photo-${PHOTO[step]}`)}>
        {step === 'domain' && <div className="FreeTrial__Card"><Testimonials /></div>}
        {step === 'researching' && <ResearchPanel domain={domain} research={research.research} />}
        {step === 'preview' && preview && values && (
          <AppPreview name={values.name} preview={preview} locations={values.locations} />
        )}
        {step === 'details' && preview && values && (
          <HeroPanel
            name={values.name}
            preview={preview}
            locationsCount={values.locations.filter((location) => location.selected).length}
            modules={values.modules}
          />
        )}
      </div>
    </div>
  );
};

export default FreeTrialContainer;
